import React, { useState, useEffect } from 'react';
import AccountList from '../components/Dashboard/AccountList';
import SyntheticAssetCreator from '../components/Dashboard/SyntheticAssetCreator';
import SynthPairCreator from '../components/Dashboard/SynthPairCreator';
import {
  fetchDeepAssets,
  fetchAccounts,
  switchMaster,
  toggleActive,
  fetchMasterStatus,
  toggleMaster,
  updateBlacklistStatus,
  getSynthPairs
} from '../services/api';
import theme from '../theme';

function ConfigPage() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [assets, setAssets] = useState([]);
  const [synthPairs, setSynthPairs] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [masterSwitch, setMasterSwitch] = useState(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSyntheticCreator, setShowSyntheticCreator] = useState(false);
  const [showSynthPairCreator, setShowSynthPairCreator] = useState(false);
  const [showSynthDropdown, setShowSynthDropdown] = useState(false);

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const fetchedAccounts = await fetchAccounts();
        setAccounts(fetchedAccounts);
      } catch (error) {
        console.error('Error loading accounts:', error);
      }
    };
    loadAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      const loadData = async () => {
        setIsLoading(true);
        try {
          const [assetsData, masterStatus, synthPairsData] = await Promise.all([
            fetchDeepAssets(selectedAccount.broker, selectedAccount.account),
            fetchMasterStatus(selectedAccount.broker),
            getSynthPairs(selectedAccount.broker, selectedAccount.account)
          ]);
          setAssets(assetsData);
          setSynthPairs(synthPairsData);
          setMasterSwitch(masterStatus.status);
        } catch (error) {
          console.error('Error loading data:', error);
        } finally {
          setIsLoading(false);
        }
      };
      loadData();
    }
  }, [selectedAccount]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSynthDropdown && !event.target.closest('.relative')) {
        setShowSynthDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSynthDropdown]);

  const handleAccountSelect = (account) => { setSelectedAccount(account); setActiveMenu(null); };
  const handleToggleMenu = (menuName) => { setActiveMenu(activeMenu === menuName ? null : menuName); setIsSelectMode(false); setSelectedAssets([]); };

  const handleToggleActiveAssetClick = async (asset) => {
    try {
      const newStatus = await toggleActive(asset.symbol || asset.epic, asset.broker, asset.account || null, asset.sectype || '');
      if (newStatus !== null) {
        setAssets(prev => prev.map(a => getAssetKey(a) === getAssetKey(asset) ? { ...a, isAssetActive: !a.isAssetActive } : a));
      }
    } catch (error) { console.error('Error toggling active:', error); }
  };

  const handleToggleAlgoAssetClick = async (asset) => {
    try {
      const res = await toggleMaster(asset.symbol || asset.epic, asset.broker, asset.account || null, asset.sectype || '');
      if (res !== null) {
        setAssets(prev => prev.map(a => getAssetKey(a) === getAssetKey(asset) ? { ...a, isAlgoActive: !a.isAlgoActive } : a));
      }
    } catch (error) { console.error('Error toggling algo:', error); }
  };

  const getAssetKey = (asset) => [asset.broker, asset.account || 'no-account', asset.symbol, asset.sectype || 'no-sectype', asset.exchange || 'no-exchange'].join('|');

  const getDisplayName = (asset) => {
    let name = '';
    if (asset.broker === 'ib') name = `${asset.account} - ${asset.symbol}`;
    else if (asset.broker === 'ig') name = asset.name;
    else name = asset.symbol;
    return name + (asset.sectype ? ` (${asset.sectype})` : '');
  };

  const addToBlacklist = async (assetsToAdd) => {
    try {
      await Promise.all(assetsToAdd.map(asset => updateBlacklistStatus(asset.broker, asset.account, asset.symbol, asset.sectype, true)));
      setAssets(prev => prev.map(asset => assetsToAdd.some(a => getAssetKey(a) === getAssetKey(asset)) ? { ...asset, isBlackList: true, isAssetActive: true } : asset));
      setSelectedAssets([]); setIsSelectMode(false);
    } catch (error) { console.error('Error adding to blacklist:', error); }
  };

  const removeFromBlacklist = async (assetsToRemove) => {
    try {
      await Promise.all(assetsToRemove.map(asset => updateBlacklistStatus(asset.broker, asset.account, asset.symbol, asset.sectype, false)));
      setAssets(prev => prev.map(asset => assetsToRemove.some(a => getAssetKey(a) === getAssetKey(asset)) ? { ...asset, isBlackList: false, isAssetActive: false } : asset));
      setSelectedAssets([]); setIsSelectMode(false);
    } catch (error) { console.error('Error removing from blacklist:', error); }
  };

  const handleMasterSwitchClick = async (broker) => {
    try { const newStatus = await switchMaster(broker); setMasterSwitch(newStatus); }
    catch (error) { console.error('Error toggling master:', error); }
  };

  const refreshData = async () => {
    if (selectedAccount) {
      setIsLoading(true);
      try {
        const [assetsData, masterStatus, synthPairsData] = await Promise.all([
          fetchDeepAssets(selectedAccount.broker, selectedAccount.account),
          fetchMasterStatus(selectedAccount.broker),
          getSynthPairs(selectedAccount.broker, selectedAccount.account)
        ]);
        setAssets(assetsData); setSynthPairs(synthPairsData); setMasterSwitch(masterStatus.status);
      } catch (error) { console.error('Error refreshing:', error); }
      finally { setIsLoading(false); }
    }
  };

  const actionBtnClass = (active) =>
    `px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-200 active:scale-[0.97] ${active ? theme.btnPrimary : theme.btnSecondary}`;

  const AssetCard = ({ asset, displayName, onClick, isSelected }) => (
    <button
      onClick={onClick}
      className={`group p-3 text-left rounded-xl border transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] ${isSelected
        ? 'bg-olive-100 border-olive-400'
        : `${theme.cardBg} border-olive-200 hover:border-olive-300`
        }`}
    >
      <div className="flex items-center gap-2">
        {isSelectMode && (
          <div className={`w-4 h-4 rounded-md border-2 flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-olive-600 border-olive-600' : 'border-olive-400 bg-white'
            }`}>
            {isSelected && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        )}
        <span className={`text-sm font-medium ${theme.textPrimary} truncate`}>{displayName}</span>
      </div>
    </button>
  );

  return (
    <div className={`min-h-screen ${theme.pageBg}`}>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Page Header */}
        <div>
          <h1 className={`text-2xl font-bold ${theme.textPrimary} tracking-wide`}>Configuration</h1>
          <p className={`text-sm ${theme.textMuted} mt-1`}>Manage brokers, assets and trading controls</p>
        </div>

        {/* Broker Selection */}
        <div className={`${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} p-5`} style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
          <h2 className={`text-xs font-semibold ${theme.textMuted} tracking-widest uppercase mb-3`}>Choose Broker</h2>
          <AccountList accounts={accounts} onSelect={handleAccountSelect} />
        </div>

        {selectedAccount && (
          <>
            {/* Actions Bar */}
            <div className={`${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} p-5`} style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
              <h2 className={`text-xs font-semibold ${theme.textMuted} tracking-widest uppercase mb-3`}>Actions</h2>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => handleToggleMenu('algo')} className={actionBtnClass(activeMenu === 'algo')}>Toggle Algo</button>
                <button onClick={() => handleToggleMenu('active')} className={actionBtnClass(activeMenu === 'active')}>Toggle Active</button>
                <button onClick={() => handleToggleMenu('blacklist')} className={actionBtnClass(activeMenu === 'blacklist')}>Manage Blacklist</button>
                <div className="relative">
                  <button onClick={() => setShowSynthDropdown(!showSynthDropdown)} className={actionBtnClass(showSynthDropdown)}>
                    Synthetic Assets
                    <svg className="w-3 h-3 ml-1.5 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {showSynthDropdown && (
                    <div className={`absolute top-full left-0 mt-1.5 w-48 ${theme.cardBg} ${theme.cardBorder} rounded-xl shadow-lg z-10 overflow-hidden`}>
                      <button onClick={() => { setShowSyntheticCreator(true); setShowSynthDropdown(false); }} className={`w-full px-4 py-2.5 text-left text-sm ${theme.textPrimary} hover:bg-olive-50 transition-colors`}>Create Asset</button>
                      <button onClick={() => { setShowSynthPairCreator(true); setShowSynthDropdown(false); }} className={`w-full px-4 py-2.5 text-left text-sm ${theme.textPrimary} hover:bg-olive-50 transition-colors border-t border-olive-100`}>Create & Manage Pairs</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex justify-center">
              <div className="w-full max-w-3xl">
                {isLoading ? (
                  <div className="text-center py-12">
                    <div className={`w-8 h-8 border-2 ${theme.spinner} rounded-full animate-spin mx-auto`} />
                    <p className={`mt-3 text-sm ${theme.textMuted}`}>Loading data from broker...</p>
                  </div>
                ) : (
                  <>
                    {(activeMenu === 'algo' || activeMenu === 'active') && (
                      <div className={`${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} p-5`} style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
                        <div className="flex justify-between items-center mb-4">
                          <h3 className={`text-sm font-bold ${theme.textPrimary}`}>Choose Symbol</h3>
                          {!isSelectMode ? (
                            <button onClick={() => setIsSelectMode(true)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${theme.btnSecondary}`}>Select</button>
                          ) : (
                            <div className="flex gap-2">
                              <button onClick={() => addToBlacklist(selectedAssets)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${theme.btnDanger}`}>Hide Selected</button>
                              <button onClick={() => setSelectedAssets(assets.filter(a => !a.isBlackList))} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${theme.btnPrimary}`}>Select All</button>
                              <button onClick={() => { setIsSelectMode(false); setSelectedAssets([]); }} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${theme.btnSecondary}`}>Cancel</button>
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {assets.filter(a => !a.isBlackList).map(asset => {
                            const suffix = activeMenu === 'algo'
                              ? (asset.isAlgoActive ? ' — TRADE' : ' — FADE')
                              : (asset.isAssetActive ? ' — ✅' : ' — ❌');
                            return (
                              <AssetCard
                                key={getAssetKey(asset)}
                                asset={asset}
                                displayName={getDisplayName(asset) + suffix}
                                isSelected={selectedAssets.some(s => getAssetKey(s) === getAssetKey(asset))}
                                onClick={() => {
                                  if (isSelectMode) {
                                    setSelectedAssets(prev => prev.some(s => getAssetKey(s) === getAssetKey(asset))
                                      ? prev.filter(s => getAssetKey(s) !== getAssetKey(asset))
                                      : [...prev, asset]);
                                  } else {
                                    activeMenu === 'algo' ? handleToggleAlgoAssetClick(asset) : handleToggleActiveAssetClick(asset);
                                  }
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {activeMenu === 'blacklist' && (
                      <div className={`${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} p-5`} style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
                        <div className="flex justify-between items-center mb-4">
                          <h3 className={`text-sm font-bold ${theme.textPrimary}`}>Blacklisted Symbols</h3>
                          <div className="flex gap-2">
                            {!isSelectMode ? (
                              <button onClick={() => setIsSelectMode(true)} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${theme.btnSecondary}`}>Select</button>
                            ) : (
                              <>
                                <button onClick={() => removeFromBlacklist(selectedAssets)} className="px-3 py-1.5 text-xs font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors">Restore Selected</button>
                                <button onClick={() => setSelectedAssets(assets.filter(a => a.isBlackList))} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${theme.btnPrimary}`}>Select All</button>
                                <button onClick={() => { setIsSelectMode(false); setSelectedAssets([]); }} className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${theme.btnSecondary}`}>Cancel</button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {assets.filter(a => a.isBlackList).map(asset => (
                            <AssetCard
                              key={getAssetKey(asset)}
                              asset={asset}
                              displayName={getDisplayName(asset)}
                              isSelected={selectedAssets.some(s => getAssetKey(s) === getAssetKey(asset))}
                              onClick={() => {
                                if (isSelectMode) {
                                  setSelectedAssets(prev => prev.some(s => getAssetKey(s) === getAssetKey(asset))
                                    ? prev.filter(s => getAssetKey(s) !== getAssetKey(asset))
                                    : [...prev, asset]);
                                }
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Master Switch */}
            {(selectedAccount.broker === 'okx' || selectedAccount.broker === 'ib') && masterSwitch !== null && (
              <div className={`${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} p-5`} style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
                <h3 className={`text-xs font-semibold ${theme.textMuted} tracking-widest uppercase mb-3 text-center`}>Master Switch</h3>
                <div className="flex justify-center">
                  <button
                    onClick={() => handleMasterSwitchClick(selectedAccount.broker)}
                    className={`px-6 py-3 rounded-xl text-sm font-bold transition-all duration-200 active:scale-[0.97] ${masterSwitch
                      ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                      : 'bg-red-500 text-white hover:bg-red-600'
                      }`}
                  >
                    {selectedAccount.broker.toUpperCase()} Master: {masterSwitch ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Modals */}
        {showSyntheticCreator && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <SyntheticAssetCreator selectedAccount={selectedAccount} onAssetCreated={refreshData} onClose={() => setShowSyntheticCreator(false)} />
          </div>
        )}
        {showSynthPairCreator && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
            <SynthPairCreator selectedAccount={selectedAccount} assets={assets} onPairCreated={refreshData} onClose={() => setShowSynthPairCreator(false)} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ConfigPage;